import React from "react";

/**
 * ChartCrosshairCursor — Recharts Tooltip 의 커스텀 cursor.
 *
 * 기본 cursor 는 활성 X(데이터 포인트)에 스냅된 세로선만 그린다. 이 컴포넌트는
 * 세로선 + **각 활성 시리즈 값의 가로선**(데이터 포인트 Y에 스냅)을 함께 그려
 * "포인트 위치 크로스헤어"를 만든다.
 *
 * 핵심: 가로선을 onMouseMove state 가 아니라 **Tooltip 의 active 상태**에서 직접
 * 그린다. cursor 는 툴팁이 활성일 때(데스크톱 hover, 모바일 탭·드래그 전부)
 * 항상 렌더되므로 플랫폼 간 동작이 동일하다(이전 onMouseMove 의존 방식은 모바일
 * 탭에서 가로선이 안 나오는 차이가 있었다 — DAS-143).
 *
 * Recharts 가 cloneElement 로 주입하는 props:
 *   - points: 세로선 양 끝점 [{x, y:top}, {x, y:top+height}] (활성 X 스냅)
 *   - top/left/width/height: 플롯 영역 offset
 *   - payload: 활성 시리즈 payload 배열 ([{dataKey, value, ...}])
 * 호출부가 추가로 내려주는 props(cloneElement 가 보존):
 *   - getDomain(dataKey) => [min, max]: 해당 시리즈 Y축의 (명시) 도메인
 *   - stroke: 디자인 토큰 색
 *
 * Y 픽셀 = top + height * (1 - (value - min)/(max - min)). YAxis 에 domain 이
 * 명시 설정(getPaddedDomain)돼 nice-rounding 이 없으므로 이 선형 매핑이 정확하다.
 */
export default function ChartCrosshairCursor({
    points,
    top,
    left,
    width,
    height,
    payload,
    stroke = "currentColor",
    getDomain,
}) {
    if (!Array.isArray(points) || points.length === 0) return null;
    const x = points[0].x;
    const yTop = top;
    const yBottom = top + height;
    const xLeft = left;
    const xRight = left + width;

    const lineProps = {
        stroke,
        strokeWidth: 1,
        strokeDasharray: "3 3",
        pointerEvents: "none",
    };

    const horizontals = [];
    const seenY = new Set();
    const items = Array.isArray(payload) ? payload : [];
    items.forEach((p, i) => {
        const v = p?.value;
        if (!Number.isFinite(v)) return;
        const dom = getDomain ? getDomain(p?.dataKey) : null;
        if (!Array.isArray(dom) || dom.length !== 2) return;
        const min = dom[0];
        const max = dom[1];
        if (!Number.isFinite(min) || !Number.isFinite(max) || max === min) return;
        const y = yTop + height * (1 - (v - min) / (max - min));
        if (y < yTop - 0.5 || y > yBottom + 0.5) return;
        const key = Math.round(y);
        if (seenY.has(key)) return; // 같은 위치 가로선 중복 제거
        seenY.add(key);
        horizontals.push(
            <line key={`h${i}`} x1={xLeft} y1={y} x2={xRight} y2={y} {...lineProps} />,
        );
    });

    return (
        <g className="recharts-tooltip-cursor">
            <line x1={x} y1={yTop} x2={x} y2={yBottom} {...lineProps} />
            {horizontals}
        </g>
    );
}
